import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../css/transferVolunteerList.module.scss";
import { TRANSFER_VOLUNTEER_PATHS, SUBJECT_PATHS } from "../../../../Routes/paths";
import { useGlobalStudy } from "../../../../context/StudyContext";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import StatCard from "../../../../Components/Common/StatCard";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import { transferVolunteerColumns } from "../helper/transfervolunteer.constant";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "../../../../common/constants/PermissionConstants";
import { perPageData, showToastMessage, GetUserData } from "../../../../common/service/CommonService";
import PinVerificationModal from "../../../Pharmacy/DailyReconsilation/components/PinVerificationModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { queryClient } from "api/queryClient";
import { useTransferVolunteerService } from "../api/useTransferVolunteerQuery";
import CommonButton from "../../../../Components/Common/CommonButton";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";

const TransferVolunteerList: React.FC = () => {
  // -------------------------------------------------------------------------------------------
  // 1. Hooks & Context
  // -------------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { selectedStudy: globalStudy } = useGlobalStudy();

  // -------------------------------------------------------------------------------------------
  // 2. States
  // -------------------------------------------------------------------------------------------
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);

  // Selected Record
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Modals
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  // Audit States
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  const userData = GetUserData();

  // -------------------------------------------------------------------------------------------
  // 3. Effects
  // -------------------------------------------------------------------------------------------
  useEffect(() => {
    document.title = "Volunteer Transfer Management | ZENTIXS ORBIT";
  }, []);

  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);

  // -------------------------------------------------------------------------------------------
  // 4. API Queries & Mutations
  // -------------------------------------------------------------------------------------------
  const { SelectAllQuery, VerifyMutation, DeleteMutation } = useTransferVolunteerService({
    searchText: searchText,
    studyid: Number(globalStudy?.studyid),
    currentPage: currentPageNumber,
    pageSize: perPageData,
    enableSelectAll: !!globalStudy?.studyid,
  });

  const { SelectAllMutation: AuditMutation } = useAuditService();

  const rawData = SelectAllQuery?.data?.data || [];
  const apiRecords = rawData.filter((r: any) => !r.summarycard);
  const summaryData = rawData.find((r: any) => r.summarycard)?.summarycard;
  const totalRecords = apiRecords[0]?.totalrecords || 0;
  const isLoading = SelectAllQuery?.isLoading || SelectAllQuery?.isFetching;

  // -------------------------------------------------------------------------------------------
  // 5. Data Processing
  // -------------------------------------------------------------------------------------------
  const totalTransferred = summaryData?.totaltransferred;
  const rescreeningCount = summaryData?.rescreeningrequired;
  const noRescreeningCount = summaryData?.norescreeningrequired;
  const verifiedCount = summaryData?.totalverifiedtransferred;

  // -------------------------------------------------------------------------------------------
  // 5. Handlers
  // -------------------------------------------------------------------------------------------
  const toggleDeleteModal = () => {
    setDeleteModal((p) => !p);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = () => setShowDeleteReason((p) => !p);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setcurrentPageNumber(1);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Volunteer Transfer",
      studyid: Number(globalStudy?.studyid)
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Volunteer_Transfer_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const handleVerifyClick = (item: any) => {
    setSelectedRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      volunteertransferid: Number(selectedRecord?.volunteertransferid || selectedRecord?.id),
      verifiedby: Number(userData?.userid),
    };

    VerifyMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["transfer-volunteer-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message || "Transfer record verified successfully!");
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error ?? "Verification failed");
      },
    });
  };

  const handleDeleteClick = (item: any) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: { id: number | null; reason: string }) => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      {
        volunteertransferid: selectedRecord.volunteertransferid || selectedRecord.id,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["transfer-volunteer-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", res?.message || "Record deleted successfully");
          setSelectedRecord(null);
          toggleDeleteModal();
          toggleDeleteReason();
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message ?? err ?? "Failed to delete record");
        },
      }
    );
  };

  const getRowActions = (row: any): any => {
    const isVerified = row?.isverified;
    const actions: any = {};

    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = (r: any) =>
        navigate(TRANSFER_VOLUNTEER_PATHS.EDIT.replace(":id", r.volunteertransferid.toString()), {
          state: { returnTo: TRANSFER_VOLUNTEER_PATHS.LIST },
        });
      actions.onDelete = (r: any) => handleDeleteClick(r);
    }
    return actions;
  };

  // -------------------------------------------------------------------------------------------
  // 6. Render
  // -------------------------------------------------------------------------------------------
  return (
    <div className="page-content">
      <Container fluid className="pb-4">
        {!globalStudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="subject transfer list" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Volunteer Transfer Management</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  Track and manage clinical trial volunteers transferred between different studies and protocols.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.TRANSFER_VOLUNTEER}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              </div>
            </div>

            {/* Stat Cards */}
            <Row className="g-3 mb-3 px-1">
              <Col md={3}>
                <StatCard
                  title="TOTAL TRANSFERRED"
                  value={totalTransferred}
                  subtext="Cumulative transfers"
                  icon={<i className="ri-swap-box-line" />}
                  bgColor="#e0f2fe"
                  iconColor="#0284c7"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                />
              </Col>

              <Col md={3}>
                <StatCard
                  title="RE-SCREENING REQUIRED"
                  value={rescreeningCount}
                  subtext="Subject safety checks needed"
                  icon={<i className="ri-shield-user-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                />
              </Col>

              <Col md={3}>
                <StatCard
                  title="NO RE-SCREENING"
                  value={noRescreeningCount}
                  subtext="Direct transfers allowed"
                  icon={<i className="ri-checkbox-circle-line" />}
                  bgColor="#ecfdf5"
                  iconColor="#059669"
                  trendType="success"
                  subtextIcon="ri-check-line"
                />
              </Col>

              <Col md={3}>
                <StatCard
                  title="VERIFIED TRANSFERS"
                  value={verifiedCount}
                  subtext="Approved by investigator"
                  icon={<i className="ri-shield-check-line" />}
                  bgColor="#f0fdf4"
                  iconColor="#16a34a"
                  trendType="success"
                  subtextIcon="ri-shield-check-fill"
                />
              </Col>
            </Row>

            {/* Table List Panel */}
            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <div className="d-flex align-items-center gap-3">
                  <h2>
                    <i className="ri-split-cells-horizontal me-2 text-primary fs-18" />
                    Volunteer Transfer Records
                  </h2>
                </div>

                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              <div className="px-4 py-2">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.TRANSFER_VOLUNTEER}
                  columns={transferVolunteerColumns}
                  data={apiRecords}
                  loading={isLoading}
                  pageSize={perPageData}
                  currentPage={currentPageNumber}
                  totalRecords={totalRecords}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                />
              </div>
            </div>
          </>
        )}
        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={handlePinVerify}
          isLoading={VerifyMutation.isPending}
          title="Verify Transfer Record"
          description={
            <>
              Please enter your password to verify study transfer record for subject:{" "}
              <strong>{selectedRecord?.initials}</strong>
            </>
          }
          isInvalid={VerifyMutation.isError}
        />
        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.volunteertransferid || selectedRecord?.id}
            recordName={selectedRecord?.initials}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation.isPending}
          />
        )}
        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default TransferVolunteerList;
